import { useReactiveVar } from "@apollo/client";
import React, { useCallback, useMemo } from "react";

import QueryProgressBarView from "./QueryProgressBarView";
import {
  getAgentConversationalName,
  getAgentIcon,
} from "@/features/knowledge-graph/utils/agentMappings";
import { collapseProgressSteps } from "@/features/knowledge-graph/utils/collapseProgressSteps";
import { getStatusIcon } from "@/features/knowledge-graph/utils/statusMappings";

import { agentProgressVar, currentStatusVar } from "@/stores/knowledgeGraph";
import { formatDurationLong } from "@/utils/timeFormatting";

/**
 * Props for QueryProgressBarContainer component
 * @property messageId - Unique identifier for the message being processed
 */
interface QueryProgressBarContainerProps {
  messageId: string | number;
}

/**
 * Container component for query progress bar displaying AI processing status
 * Manages agent progress tracking, status calculation, and duration formatting
 * @remarks Uses Apollo reactive variables for state management
 * @param root0 - Component props
 * @param root0.messageId - Unique identifier for the message being processed
 */
const QueryProgressBarContainer = ({
  messageId: _messageId,
}: QueryProgressBarContainerProps) => {
  const agentProgress = useReactiveVar(agentProgressVar);
  const currentStatus = useReactiveVar(currentStatusVar);

  const retryAttempt = useMemo(() => {
    const currentAgent = agentProgress.filter(
      it => it.status === "complete" && it.agent === "incrementRetryCount"
    );
    return currentAgent.length > 0 ? currentAgent.length : 1;
  }, [agentProgress]);

  const getAgentIconWrapper = useCallback(
    (agent: string): string => getAgentIcon(agent),
    []
  );

  const getStatusIconWrapper = useCallback(
    (status: string): string => getStatusIcon(status, true), // Use conversational style
    []
  );

  const formatAgentName = useCallback(
    (agent: string): string => getAgentConversationalName(agent),
    []
  );

  const formatDuration = useCallback(
    (duration: number): string => formatDurationLong(duration),
    []
  );

  const getConversationalMessage = useCallback(
    (agent: string, status: string): string => {
      const messages: Record<string, Record<string, string>> = {
        decide: {
          running: "Figuring out the best way to help...",
          complete: "Got it! I know what you need",
          error: "Had trouble understanding the question",
        },
        generateSQLWithLangChain: {
          running: "Looking for the right information...",
          complete: "Found where to look!",
          error: "Couldn't figure out where to find this",
        },
        executeSQL: {
          running: "Checking our records for you...",
          complete: "Found the data!",
          error: "Had trouble accessing the data",
        },
        retrieveKB: {
          running: "Searching through our knowledge...",
          complete: "Found relevant information!",
          error: "Couldn't search the knowledge base",
        },
        fetchReports: {
          running: "Looking through available reports...",
          complete: "Found some reports!",
          error: "Couldn't access the reports",
        },
        knowledge: {
          running: "Searching online for more info...",
          complete: "Found additional information!",
          error: "Had trouble searching online",
        },
        aggregate: {
          running: "Putting everything together for you...",
          complete: "Ready with your answer!",
          error: "Had trouble combining the information",
        },
        incrementRetryCount: {
          running: "Trying again to retrieve the information...",
          complete: `Retrying query... (Attempt ${retryAttempt})`,
          error: "Unable to retrieve the information after several attempts.",
        },
      };
      return messages[agent]?.[status] || "";
    },
    [retryAttempt]
  );

  const progressData = useMemo(() => {
    // SE-8646 / SE-8783: one row per label as the user reads it — every row
    // with the same label (several unmapped tool calls, a reasoning step the
    // agent loop runs again) is one step, shown where it first appeared.
    const steps = collapseProgressSteps(
      agentProgress,
      getAgentConversationalName
    );
    const totalAgents = steps.length;
    const completedAgents = steps.filter(
      agent => agent.status === "complete"
    ).length;
    // Read off the raw rows: an error inside a merged step that is still
    // running is still an error the status text should admit to.
    const hasError = agentProgress.some(agent => agent.status === "error");
    const hasRunningStep = steps.some(agent => agent.status === "running");

    const isComplete = completedAgents === totalAgents && totalAgents > 0;

    // Enhanced agent progress with conversational messages
    const enhancedAgentProgress = steps.map(agent => ({
      ...agent,
      message: getConversationalMessage(agent.agent, agent.status),
    }));

    // Compute overall status based on current state
    const overallStatus = hasError
      ? "😕 I ran into a problem, but here's what I have..."
      : isComplete
        ? "✨ Here's what I found!"
        : hasRunningStep
          ? "💭 Working on your request..."
          : totalAgents === 0
            ? "🎯 Getting ready to help you..."
            : currentStatus?.message || "Getting started...";

    return {
      agentProgress: enhancedAgentProgress,
      statusText: overallStatus,
      isComplete,
      hasError,
      totalAgents,
      completedAgents,
    };
  }, [agentProgress, currentStatus, getConversationalMessage]);

  return (
    <QueryProgressBarView
      {...progressData}
      getAgentIcon={getAgentIconWrapper}
      getStatusIcon={getStatusIconWrapper}
      formatAgentName={formatAgentName}
      formatDuration={formatDuration}
    />
  );
};

export default QueryProgressBarContainer;
